import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, Cell, CartesianGrid, PieChart, Pie, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { timeAgo } from '../../utils/dateUtils';
import usePagination from '../../hooks/usePagination';
import AccountsTable from './AccountsTable';
import SubscriptionPlanEditor from './SubscriptionPlanEditor';

// Drives the coloured dot on each audit entry. Keys match backend utils/auditLog.js's
// ACTIONS; anything unmapped falls back to the neutral 'info' tone.
const ACTIVITY_TONES = {
  'account.created': 'success',
  'account.updated': 'info',
  'account.deleted': 'error',
  'report.resolved': 'success',
  'report.reopened': 'info',
};

const roles = ['user', 'special', 'staff', 'admin'];
const packages = ['free', 'pro', 'advanced'];
const emptyForm = { username: '', email: '', password: '', role: 'user', subscriptionPackage: 'free', subscriptionPurchasedAt: '' };

const roleColors = {
  admin: 'var(--danger-color)',
  staff: 'var(--orchid-color)',
  special: 'var(--indigo-color)',
  user: 'color-mix(in srgb, var(--text-color) 35%, transparent)',
};

function toDateInputValue(value) {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}

function RoleTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0];
  return (
    <div className="chart-tooltip">
      <strong>{entry.payload.role}</strong>
      <span>{entry.value} account{entry.value === 1 ? '' : 's'}</span>
    </div>
  );
}

function MonthlyUsersTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <strong>{label}</strong>
      <span>{payload[0].value} new account{payload[0].value === 1 ? '' : 's'}</span>
    </div>
  );
}

function AdminPage() {
  const { t } = useTranslation('admin');
  const { accounts, username: currentUsername, adminCreateAccount, adminUpdateAccount, adminDeleteAccount } = useAuth();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  // Bumped after any account mutation so the audit panel picks up the entry the
  // server just wrote, without reloading the page.
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [adminSearch, setAdminSearch] = useState('');
  const [staffSearch, setStaffSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const counts = useMemo(() => ({
    total: accounts.length,
    admin: accounts.filter((item) => item.role === 'admin').length,
    staff: accounts.filter((item) => item.role === 'staff').length,
    user: accounts.filter((item) => item.role === 'user').length,
    special: accounts.filter((item) => item.role === 'special').length,
  }), [accounts]);

  const roleChartData = useMemo(() => ([
    { role: t('roleLabels.admin'), key: 'admin', value: counts.admin },
    { role: t('roleLabels.staff'), key: 'staff', value: counts.staff },
    { role: t('roleLabels.user'), key: 'user', value: counts.user },
    { role: t('roleLabels.special'), key: 'special', value: counts.special },
  ].filter((entry) => entry.value > 0)), [counts, t]);

  const monthlyData = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, index) => {
      const month = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      const label = month.toLocaleString('en', { month: 'short' });
      const total = accounts.filter((account) => {
        if (!account.createdAt) return false;
        const created = new Date(account.createdAt);
        return created.getFullYear() === month.getFullYear() && created.getMonth() === month.getMonth();
      }).length;
      return { name: label, total };
    });
  }, [accounts]);

  const hasMonthlyData = monthlyData.some((entry) => entry.total > 0);
  const currentMonthCount = monthlyData[monthlyData.length - 1]?.total || 0;
  const previousMonthCount = monthlyData[monthlyData.length - 2]?.total || 0;
  const monthlyDelta = currentMonthCount - previousMonthCount;

  const adminAccounts = useMemo(
    () => accounts.filter((item) => item.role === 'admin' && item.username.toLowerCase().includes(adminSearch.trim().toLowerCase())),
    [accounts, adminSearch],
  );
  const staffAccounts = useMemo(
    () => accounts.filter((item) => item.role === 'staff' && item.username.toLowerCase().includes(staffSearch.trim().toLowerCase())),
    [accounts, staffSearch],
  );
  const userAccounts = useMemo(
    () => accounts.filter((item) => (item.role === 'user' || item.role === 'special') && item.username.toLowerCase().includes(userSearch.trim().toLowerCase())),
    [accounts, userSearch],
  );
  const adminPagination = usePagination(adminAccounts, 10, adminSearch);
  const staffPagination = usePagination(staffAccounts, 10, staffSearch);
  const userPagination = usePagination(userAccounts, 10, userSearch);

  // Server-side audit trail. This used to filter the ToastContext history, which lives
  // in localStorage and therefore only ever contained actions taken in *this* browser -
  // an admin opening the dashboard anywhere else saw an empty panel, and a staff
  // member's actions were never visible to an admin at all.
  const [activity, setActivity] = useState([]);
  const [activityLoaded, setActivityLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/users/activity')
      .then(({ activity: fetched }) => {
        if (cancelled) return;
        setActivity(fetched);
        setActivityLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setActivity([]);
        setActivityLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activityRefreshKey]);

  const describeActivity = (item) => {
    // Falls back to the raw dotted action so a newly added ACTIONS entry shows up as
    // something readable rather than a blank line if its i18n key is missing.
    const key = `admin.activity.${item.action}`;
    const label = t(key, { target: item.targetLabel, defaultValue: '' });
    return label || `${item.action} — ${item.targetLabel}`;
  };

  const openAddAccount = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEditAccount = (account) => {
    setEditingId(account.id);
    setForm({
      username: account.username,
      email: account.email || '',
      password: '',
      role: account.role,
      subscriptionPackage: account.subscriptionPackage || 'free',
      subscriptionPurchasedAt: toDateInputValue(account.subscriptionPurchasedAt),
    });
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (editingId) {
      const updates = {
        username: form.username,
        email: form.email,
        role: form.role,
        subscriptionPackage: form.subscriptionPackage,
        subscriptionPurchasedAt: form.subscriptionPurchasedAt || null,
      };
      if (form.password) updates.password = form.password;
      const result = await adminUpdateAccount(editingId, updates);
      if (!result.success) {
        setError(result.error);
        return;
      }
      addToast(t('admin.toast.accountUpdated'), 'success');
      setActivityRefreshKey((key) => key + 1);
    } else {
      if (!form.password) {
        setError(t('admin.passwordRequired'));
        return;
      }
      const result = await adminCreateAccount({ ...form, subscriptionPurchasedAt: form.subscriptionPurchasedAt || null });
      if (!result.success) {
        setError(result.error);
        return;
      }
      addToast(t('admin.toast.accountCreated'), 'success');
      setActivityRefreshKey((key) => key + 1);
    }
    closeForm();
  };

  const handleDelete = async (account) => {
    const confirmed = await confirm(t('admin.confirmDelete', { username: account.username }));
    if (!confirmed) return;
    const result = await adminDeleteAccount(account.id);
    if (!result.success) {
      addToast(result.error, 'error');
      return;
    }
    addToast(t('admin.toast.accountDeleted'), 'success');
    setActivityRefreshKey((key) => key + 1);
  };

  const handleRowClick = (account) => {
    navigate(`/admin/accounts/${account.id}`);
  };

  useEffect(() => {
    if (!showForm) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeForm();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showForm]);

  return (
    <section className="page-card">
      <div className="page-header">
        <div>
          <p className="eyebrow">{t('admin.eyebrow')}</p>
          <h2>{t('admin.heading')}</h2>
        </div>
        <div className="page-actions">
          <button onClick={openAddAccount} className="primary-btn standout-btn">
            <span aria-hidden="true">+</span> {t('admin.addAccount')}
          </button>
        </div>
      </div>

      <div className="summary-grid compact-stats">
        <article className="summary-card stat-card tint-lavender">
          <div className="stat-card-top">
            <span className="stat-icon" aria-hidden="true">👥</span>
            <span className="stat-label">{t('admin.stats.totalAccounts')}</span>
          </div>
          <strong>{counts.total}</strong>
        </article>
        <article className="summary-card stat-card tint-orchid">
          <div className="stat-card-top">
            <span className="stat-icon" aria-hidden="true">🛡️</span>
            <span className="stat-label">{t('admin.stats.admins')}</span>
          </div>
          <strong>{counts.admin}</strong>
        </article>
        <article className="summary-card stat-card tint-pink">
          <div className="stat-card-top">
            <span className="stat-icon" aria-hidden="true">🧑‍💼</span>
            <span className="stat-label">{t('admin.stats.staff')}</span>
          </div>
          <strong>{counts.staff}</strong>
        </article>
        <article className="summary-card stat-card tint-indigo">
          <div className="stat-card-top">
            <span className="stat-icon" aria-hidden="true">🙋</span>
            <span className="stat-label">{t('admin.stats.customers')}</span>
          </div>
          <strong>{counts.user + counts.special}</strong>
        </article>
      </div>

      <div className="charts-grid">
        <div className="sheet-panel">
          <div className="sheet-panel-header"><span>{t('admin.accountsByRole')}</span></div>
          <div className="sheet-panel-body">
            {roleChartData.length === 0 ? (
              <p className="chart-empty">{t('admin.noAccountsYet')}</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Tooltip content={<RoleTooltip />} />
                    <Pie data={roleChartData} dataKey="value" nameKey="role" innerRadius={55} outerRadius={85} paddingAngle={3}>
                      {roleChartData.map((entry) => <Cell key={entry.key} fill={roleColors[entry.key]} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="chart-legend">
                  {roleChartData.map((entry) => (
                    <span className="chart-legend-item" key={entry.key}>
                      <i className="legend-dot" style={{ background: roleColors[entry.key] }} />
                      {entry.role} ({entry.value})
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="sheet-panel">
          <div className="sheet-panel-header">
            <span>{t('admin.newAccountsPerMonth')}</span>
            {hasMonthlyData && (
              <span className={`stat-trend${monthlyDelta > 0 ? ' growth-up' : monthlyDelta < 0 ? ' growth-down' : ''}`}>
                {monthlyDelta > 0 ? '▲' : monthlyDelta < 0 ? '▼' : '—'} {Math.abs(monthlyDelta)} {t('admin.vsLastMonth')}
              </span>
            )}
          </div>
          <div className="sheet-panel-body">
            {!hasMonthlyData ? (
              <p className="chart-empty">{t('admin.noSignupsYet')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyData} barCategoryGap="32%">
                  <defs>
                    <linearGradient id="userTrendGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--primary-color)" />
                      <stop offset="100%" stopColor="var(--secondary-color)" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--text-color)" fontSize={11} axisLine={false} tickLine={false} />
                  <YAxis stroke="var(--text-color)" width={30} allowDecimals={false} fontSize={11} axisLine={false} tickLine={false} />
                  <Tooltip content={<MonthlyUsersTooltip />} cursor={{ fill: 'color-mix(in srgb, var(--primary-color) 8%, transparent)' }} />
                  <Bar dataKey="total" radius={[8, 8, 0, 0]} barSize={26}>
                    {monthlyData.map((entry, index) => (
                      <Cell key={entry.name + index} fill={index === monthlyData.length - 1 ? 'url(#userTrendGradient)' : 'color-mix(in srgb, var(--primary-color) 22%, transparent)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <SubscriptionPlanEditor />

      <div className="sheet-tables-grid">
        <AccountsTable
          title={t('admin.staffAccounts')}
          tint="orchid"
          accounts={staffPagination.pageItems}
          currentUsername={currentUsername}
          showActions
          showSubscription
          showLastLogin
          onEdit={openEditAccount}
          onDelete={handleDelete}
          onRowClick={handleRowClick}
          searchValue={staffSearch}
          onSearchChange={setStaffSearch}
          searchPlaceholder={t('admin.searchStaff')}
          emptyMessage={t('admin.noStaffAccounts')}
          pagination={{ page: staffPagination.page, totalPages: staffPagination.totalPages, onChange: staffPagination.setPage }}
        />

        <div className="sheet-table-card">
          <div className="sheet-panel-header tint-mint"><span>{t('admin.staffActivity')}</span></div>
          <div className="activity-list">
            {activityLoaded && activity.length === 0 && <p className="goal-list-empty">{t('admin.noStaffActivity')}</p>}
            {activity.map((item) => (
              <div key={item.id} className={`activity-item activity-${ACTIVITY_TONES[item.action] || 'info'}`}>
                <span className="activity-dot" aria-hidden="true" />
                <div className="activity-item-body">
                  <span className="activity-message"><strong>{item.actorUsername}</strong> — {describeActivity(item)}</span>
                  <span className="activity-time">{timeAgo(item.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <AccountsTable
          title={t('admin.adminAccounts')}
          tint="rose"
          accounts={adminPagination.pageItems}
          currentUsername={currentUsername}
          showActions
          showSubscription
          showLastLogin
          onEdit={openEditAccount}
          onDelete={handleDelete}
          onRowClick={handleRowClick}
          searchValue={adminSearch}
          onSearchChange={setAdminSearch}
          searchPlaceholder={t('admin.searchAdmins')}
          emptyMessage={t('admin.noAdminAccounts')}
          pagination={{ page: adminPagination.page, totalPages: adminPagination.totalPages, onChange: adminPagination.setPage }}
        />

        <AccountsTable
          title={t('admin.userAccounts')}
          tint="lavender"
          accounts={userPagination.pageItems}
          currentUsername={currentUsername}
          showActions
          showSubscription
          showLastLogin
          onEdit={openEditAccount}
          onDelete={handleDelete}
          onRowClick={handleRowClick}
          searchValue={userSearch}
          onSearchChange={setUserSearch}
          searchPlaceholder={t('admin.searchUsers')}
          emptyMessage={t('admin.noUserAccounts')}
          pagination={{ page: userPagination.page, totalPages: userPagination.totalPages, onChange: userPagination.setPage }}
        />
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <form onSubmit={handleSubmit} className="card form-card modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? t('admin.editAccount') : t('admin.addAccount')}</h3>
            <label>
              {t('admin.form.username')}
              <input autoFocus value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} />
            </label>
            <label>
              {t('admin.form.email')}
              <input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
            </label>
            <label>
              {editingId ? t('admin.form.newPasswordHint') : t('admin.form.password')}
              <input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
            </label>
            <label>
              {t('admin.form.role')}
              <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}>
                {roles.map((role) => <option key={role} value={role}>{t(`roleLabels.${role}`)}</option>)}
              </select>
            </label>
            <label>
              {t('admin.form.subscriptionPackage')}
              <select value={form.subscriptionPackage} onChange={(event) => setForm({ ...form, subscriptionPackage: event.target.value })}>
                {packages.map((pkg) => <option key={pkg} value={pkg}>{t(`packageLabels.${pkg}`)}</option>)}
              </select>
            </label>
            <label>
              {t('admin.form.subscriptionPurchasedOn')}
              <input type="date" value={form.subscriptionPurchasedAt} onChange={(event) => setForm({ ...form, subscriptionPurchasedAt: event.target.value })} />
            </label>
            {error && <span className="error-text">{error}</span>}
            <div className="modal-actions">
              <button type="button" className="text-btn" onClick={closeForm}>{t('admin.form.cancel')}</button>
              <button className="primary-btn standout-btn" type="submit">{editingId ? t('admin.form.saveChanges') : t('admin.form.addAccount')}</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default AdminPage;
